import { lazy, Suspense, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CirclePlay, ExternalLink, LogIn } from 'lucide-react';
import { Icone } from '../components/Icone';
import { IconePleine } from '../components/IconePleine';
import { Chargement, EcranEnErreur, MessageErreur, Pastille } from '../components/ui/Primitives';
import { PlanFormation } from '../components/elearning/PlanFormation';
import { BandeauIndisponible } from '../components/lecteur/BandeauIndisponible';
import { CoursEnLigne } from '../components/lecteur/CoursEnLigne';
import { LeconEnLecture } from '../components/lecteur/LeconEnLecture';
import { LecteurVideo } from '../components/lecteur/LecteurVideo';
import { MesNotes } from '../components/lecteur/MesNotes';
import { heures, useMaFormationApprenant } from '../lib/useEspaceApprenant';
import {
  LIBELLES_DEMI_JOURNEE,
  dateCourte,
  dureeLisible,
  heureReunion,
  jetonApprenant,
  urlVideoReplay,
  useElearningApprenant,
  useEmargerADistance,
  type ReplayApprenant,
} from '../lib/useElearning';
import { ErreurApi } from '../lib/api';
import { clavierOnglets } from '../components/ui/onglets-aria';

// La salle LiveKit n'est chargée qu'à l'entrée en classe.
const SalleDirect = lazy(() => import('../components/elearning/SalleDirect'));

/**
 * Lecteur de cours (écran `player` de la maquette validée,
 * `docs/maquette/modules/04-pages-pedagogie-player.js`).
 *
 * `?lecon=` ouvre une leçon de cours en ligne dans le lecteur vidéo (TF-444,
 * `LeconEnLecture`). Sans leçon, l'apprenant trouve les cours publiés de sa
 * formation (« Cours en ligne ») et ses classes à distance (CDC §7.2).
 *
 * Il y rejoint la classe en direct dans la salle LiveKit — caméra et micro
 * obligatoires — et y émarge d'un geste, caméra allumée (CDC §6.7) ; il relit
 * les replays de sa promotion, rangés par bloc du plan
 * de formation. Classes, replays et plan viennent de `/espace-apprenant/elearning` ;
 * la formation et les heures émargées de `/espace-apprenant/formation`, comme
 * `MesFormationsPage`.
 *
 * « Mes notes » rassemble les notes personnelles prises sous la vidéo des
 * leçons (TF-446) ; `?onglet=notes` l'ouvre d'emblée, comme `?onglet=classes`
 * les classes à distance (depuis le tableau de bord, TF-450).
 *
 * Ressources, transcription et discussion n'ont pas de donnée en base pour les
 * classes à distance : elles restent annoncées indisponibles, jamais remplies
 * de données fictives.
 */

type CleOnglet =
  | 'cours'
  | 'classes'
  | 'replays'
  | 'apercu'
  | 'notes'
  | 'ressources'
  | 'transcription'
  | 'discussion';

const ONGLETS: { cle: CleOnglet; libelle: string }[] = [
  { cle: 'cours', libelle: 'Cours en ligne' },
  { cle: 'classes', libelle: 'Classes à distance' },
  { cle: 'replays', libelle: 'Replays' },
  { cle: 'apercu', libelle: "Vue d'ensemble" },
  { cle: 'notes', libelle: 'Mes notes' },
  { cle: 'ressources', libelle: 'Ressources' },
  { cle: 'transcription', libelle: 'Transcription' },
  { cle: 'discussion', libelle: 'Discussion' },
];

export function LecteurCoursPage(): JSX.Element {
  const [parametres] = useSearchParams();
  const leconId = parametres.get('lecon');
  return leconId ? <LeconEnLecture key={leconId} leconId={leconId} /> : <EspaceLecteur />;
}

function EspaceLecteur(): JSX.Element {
  const naviguer = useNavigate();
  const [parametres] = useSearchParams();
  const [onglet, setOnglet] = useState<CleOnglet>(() => {
    const demande = parametres.get('onglet');
    return demande === 'notes' || demande === 'classes' ? demande : 'cours';
  });
  const [replay, setReplay] = useState<ReplayApprenant | null>(null);
  const [enSalle, setEnSalle] = useState<string | null>(null);
  // Vus pendant cette visite : l'API ne le saura qu'au prochain chargement.
  const [vusIci, setVusIci] = useState<ReadonlySet<string>>(new Set());
  const { data, isLoading, error } = useMaFormationApprenant();
  const distance = useElearningApprenant();
  const emarger = useEmargerADistance();
  const elearning = distance.data;

  if (isLoading) return <Chargement libelle="Chargement de votre formation…" />;
  if (error) {
    return (
      <EcranEnErreur
        titre="Cours en ligne"
        message={error instanceof ErreurApi
            ? error.message
            : 'Votre formation n’a pas pu être chargée.'}
      />
    );
  }
  if (!data) return <EcranEnErreur titre="Cours en ligne" message="Votre formation n’a pas pu être chargée." />;

  const { formation, emargement } = data;
  const enDirect = elearning?.enDirect ?? null;
  const prochaine = elearning?.classes.find((classe) => classe.etat === 'PROGRAMMEE') ?? null;
  const classeEnSalle = elearning?.classes.find((classe) => classe.seanceId === enSalle) ?? null;

  const lire = (choisi: ReplayApprenant) => {
    setVusIci((actuels) => new Set(actuels).add(choisi.id));
    if (choisi.lecture) {
      setReplay(choisi);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else if (choisi.url) {
      window.open(choisi.url, '_blank', 'noopener,noreferrer');
    }
  };

  const avancement =
    formation && formation.heuresPrevues > 0
      ? Math.min(100, Math.round((emargement.heuresSuivies / formation.heuresPrevues) * 100))
      : null;

  return (
    <div className="min-h-screen bg-codialis-ink">
      <div className="sticky top-0 z-20 border-b border-gray-200 bg-white">
        <div className="flex flex-wrap items-center gap-4 px-4 sm:px-6 py-3">
          <button
            type="button"
            onClick={() => naviguer('/mycourses')}
            className="flex items-center gap-2 text-sm font-medium text-codialis-navy hover:text-codialis-sky"
          >
            <Icone nom="chevronGauche" />
            Retour
          </button>
          <div className="h-6 w-px bg-gray-200" />
          <div className="min-w-0 flex-1">
            <div className="text-xs text-codialis-gray">
              {formation ? `${formation.code} · ${formation.niveau}` : 'Aucune formation rattachée'}
            </div>
            <h1 className="font-display truncate font-bold text-codialis-navy">
              {formation ? formation.titre : 'Lecteur de cours'}
            </h1>
          </div>
          {avancement !== null && (
            <div className="hidden items-center gap-2 text-xs text-codialis-gray md:flex">
              <IconePleine nom="cochePleine" className="h-4 w-4 text-codialis-mint" />
              {avancement}% · {heures(emargement.heuresSuivies)} suivies sur {formation?.heuresPrevues} h
            </div>
          )}
          <button
            type="button"
            onClick={() => naviguer('/mycourses')}
            className="flex items-center gap-2 rounded-lg bg-codialis-navy px-4 py-2 text-sm font-semibold text-white transition hover:bg-codialis-deep"
          >
            Voir ma formation
            <Icone nom="chevronDroit" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 min-h-[calc(100vh-110px)] gap-0 lg:grid-cols-4">
        <div className="bg-codialis-ink lg:col-span-3 min-w-0">
          {replay ? (
            <div className="bg-black">
              <LecteurVideo
                key={replay.id}
                source={urlVideoReplay(replay.id, 'apprenant')}
                titre={replay.titre}
                dureeConnue={replay.dureeSecondes}
                autoPlay
              />
              <div className="flex flex-wrap items-center gap-3 px-4 py-3 text-white sm:px-6">
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold">{replay.titre}</div>
                  <div className="text-xs text-white/60">
                    Replay de la classe du {dateCourte(replay.date)}
                    {dureeLisible(replay.dureeSecondes) ? ` · ${dureeLisible(replay.dureeSecondes)}` : ''}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setReplay(null)}
                  className="min-h-[40px] rounded-lg px-3 text-xs font-semibold text-white/80 hover:bg-white/10"
                >
                  Fermer le replay
                </button>
              </div>
            </div>
          ) : (
            <div className="video-area relative flex aspect-video items-center justify-center p-4 text-center sm:p-6">
              {enDirect ? (
                <div className="max-w-md text-white">
                  <span className="mb-3 inline-flex items-center gap-2 rounded-lg bg-red-600 px-2.5 py-1 text-xs font-bold">
                    <span className="h-2 w-2 animate-pulse rounded-full bg-white" aria-hidden />
                    EN DIRECT
                  </span>
                  <div className="font-display text-lg font-bold">{enDirect.libelle}</div>
                  <div className="mt-1 text-sm text-white/70">
                    {enDirect.heureDebut}–{enDirect.heureFin}
                    {enDirect.formateur ? ` · ${enDirect.formateur}` : ''}
                  </div>
                  <button
                    type="button"
                    onClick={() => setEnSalle(enDirect.seanceId)}
                    className="gradient-mint mt-4 inline-flex min-h-[48px] items-center gap-2 rounded-xl px-5 text-sm font-semibold text-codialis-navy"
                  >
                    <LogIn className="h-4 w-4" aria-hidden />
                    Rejoindre la classe
                  </button>
                  <p className="mt-3 text-xs text-white/60">
                    {enDirect.emargement.emargeLe
                      ? `Émargé à ${heureReunion(enDirect.emargement.emargeLe)}. Caméra et micro obligatoires pendant la classe.`
                      : 'Caméra et micro obligatoires : vous émargez dans la salle, caméra allumée.'}
                  </p>
                </div>
              ) : (
                <div className="max-w-md text-white/80">
                  <IconePleine nom="lecturePleine" className="mx-auto mb-3 h-10 w-10 text-white/30" />
                  <p className="text-sm leading-relaxed">
                    {prochaine
                      ? `Prochaine classe à distance : ${prochaine.libelle}, le ${dateCourte(prochaine.date)} de ${prochaine.heureDebut} à ${prochaine.heureFin}.`
                      : 'Aucune classe à distance n’est programmée pour le moment.'}
                  </p>
                  {elearning && elearning.replays.length > 0 && (
                    <p className="mt-2 text-xs text-white/60">Choisissez un replay pour le revoir.</p>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="bg-white">
            <div
              role="tablist"
              aria-label="Contenu du module"
              onKeyDown={clavierOnglets(
                ONGLETS.map((entree) => entree.cle),
                onglet,
                setOnglet,
              )}
              className="relative flex gap-6 overflow-x-auto border-b border-gray-200 px-4 sm:px-6"
            >
              {ONGLETS.map((entree) => (
                <button
                  key={entree.cle}
                  type="button"
                  role="tab"
                  id={`onglet-${entree.cle}`}
                  aria-selected={onglet === entree.cle}
                  aria-controls={`panneau-${entree.cle}`}
                  tabIndex={onglet === entree.cle ? 0 : -1}
                  data-onglet={entree.cle}
                  onClick={() => setOnglet(entree.cle)}
                  className={`tab-btn shrink-0 whitespace-nowrap border-b-2 py-4 text-sm ${
                    onglet === entree.cle ? 'active' : ''
                  }`}
                >
                  {entree.libelle}
                </button>
              ))}
            </div>

            <div className="p-4 sm:p-6">
              {onglet === 'cours' && (
                <div role="tabpanel" id="panneau-cours" aria-labelledby="onglet-cours">
                  <h2 className="font-display mb-3 text-xl font-bold text-codialis-navy">Mes cours en ligne</h2>
                  <CoursEnLigne />
                </div>
              )}

              {onglet === 'classes' && (
                <div role="tabpanel" id="panneau-classes" aria-labelledby="onglet-classes">
                  <h2 className="font-display mb-3 text-xl font-bold text-codialis-navy">
                    Mes classes à distance
                  </h2>
                  {distance.isLoading && <Chargement libelle="Chargement de vos classes…" />}
                  {distance.error && (
                    <MessageErreur
                      message={
                        distance.error instanceof ErreurApi
                          ? distance.error.message
                          : 'Vos classes n’ont pas pu être chargées.'
                      }
                    />
                  )}
                  {elearning && !elearning.diffusion.raccordee && (
                    <p className="mb-3 rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-900">
                      La diffusion en direct n’est pas encore disponible sur la plateforme.
                    </p>
                  )}
                  {elearning && elearning.classes.length === 0 && (
                    <p className="text-sm text-codialis-gray">
                      Aucune classe à distance programmée ni tenue cette semaine.
                    </p>
                  )}
                  <ul className="divide-y divide-gray-100">
                    {elearning?.classes.map((classe) => (
                      <li key={classe.seanceId} className="flex flex-wrap items-center gap-3 py-3">
                        <div className="w-24 shrink-0 font-mono text-xs text-codialis-gray">
                          {dateCourte(classe.date)}
                          <span className="block text-[10px]">{LIBELLES_DEMI_JOURNEE[classe.demiJournee]}</span>
                        </div>
                        <div className="min-w-0 flex-1 basis-40">
                          <div className="truncate text-sm font-semibold text-codialis-navy">{classe.libelle}</div>
                          <div className="truncate text-xs text-codialis-gray">
                            {classe.heureDebut}–{classe.heureFin}
                            {classe.bloc && classe.bloc !== classe.libelle ? ` · ${classe.bloc}` : ''}
                            {classe.formateur ? ` · ${classe.formateur}` : ''}
                          </div>
                        </div>
                        <Pastille texte={classe.etatLibelle} ton={classe.etatTon} />
                        {classe.etat === 'EN_DIRECT' && classe.refusRejoindre === null && (
                          <button
                            type="button"
                            onClick={() => setEnSalle(classe.seanceId)}
                            className="inline-flex min-h-[44px] items-center gap-2 rounded-xl bg-codialis-navy px-4 text-sm font-semibold text-white"
                          >
                            <LogIn className="h-4 w-4" aria-hidden />
                            Rejoindre
                          </button>
                        )}
                        {classe.etat === 'EN_DIRECT' && classe.refusRejoindre && (
                          <span className="basis-full text-xs text-codialis-gray">{classe.refusRejoindre}</span>
                        )}
                        {classe.emargement.emargeLe && (
                          <span className="basis-full text-xs font-semibold text-codialis-teal">
                            Émargé à {heureReunion(classe.emargement.emargeLe)}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {onglet === 'replays' && (
                <div role="tabpanel" id="panneau-replays" aria-labelledby="onglet-replays">
                  <h2 className="font-display mb-3 text-xl font-bold text-codialis-navy">
                    Replays de ma promotion
                  </h2>
                  {elearning && elearning.replays.length === 0 && (
                    <p className="text-sm text-codialis-gray">
                      Aucun replay pour le moment : les classes enregistrées apparaissent ici.
                    </p>
                  )}
                  <ul className="divide-y divide-gray-100">
                    {elearning?.replays.map((choisi) => (
                      <li key={choisi.id}>
                        <button
                          type="button"
                          onClick={() => lire(choisi)}
                          className="flex w-full items-center gap-3 py-3 text-left transition hover:bg-gray-50"
                        >
                          {choisi.lecture ? (
                            <CirclePlay className="h-5 w-5 shrink-0 text-codialis-sky" aria-hidden />
                          ) : (
                            <ExternalLink className="h-5 w-5 shrink-0 text-codialis-sky" aria-hidden />
                          )}
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm font-medium text-codialis-navy">{choisi.titre}</span>
                            <span className="block text-xs text-codialis-gray">
                              {dateCourte(choisi.date)} · {choisi.classe}
                              {dureeLisible(choisi.dureeSecondes) ? ` · ${dureeLisible(choisi.dureeSecondes)}` : ''}
                            </span>
                          </span>
                          {!choisi.vu && !vusIci.has(choisi.id) && <Pastille texte="Nouveau" ton="mint" />}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {onglet === 'apercu' && (
                <div role="tabpanel" id="panneau-apercu" aria-labelledby="onglet-apercu">
                  <h2 className="font-display mb-3 text-xl font-bold text-codialis-navy">
                    À propos de cette formation
                  </h2>
                  {!formation ? (
                    <p className="text-sm text-codialis-gray">
                      Aucune formation n’est encore rattachée à votre dossier.
                    </p>
                  ) : (
                    <>
                      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        {(
                          [
                            ['Durée prévue', `${formation.heuresPrevues} h`],
                            ['Niveau', formation.niveau],
                            ['Certification visée', formation.rncp],
                            ['Code diplôme', formation.codeDiplome],
                          ] as const
                        ).map(([libelle, valeur]) => (
                          <div key={libelle} className="rounded-xl bg-gray-50 p-3">
                            <div className="text-xs uppercase text-codialis-gray">{libelle}</div>
                            <div className="font-display font-bold text-codialis-navy">
                              {valeur}
                            </div>
                          </div>
                        ))}
                      </div>

                      <h3 className="font-display mb-3 font-bold text-codialis-navy">
                        Évaluation Qualiopi de ce module
                      </h3>
                      <div className="flex flex-col items-start gap-4 rounded-xl border border-codialis-mint/20 bg-gradient-to-r from-codialis-mint/5 to-codialis-sky/5 p-4 sm:flex-row sm:items-center">
                        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-codialis-mint/15">
                          <Icone nom="bouclierCoche" className="h-6 w-6 text-codialis-mint" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-semibold text-codialis-navy">
                            Évaluation à chaud disponible en fin de module
                          </div>
                          <div className="text-xs text-codialis-gray">
                            Critère Qualiopi 11 · Recueil de l'appréciation des bénéficiaires
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => naviguer('/app-questionnaires')}
                          className="text-xs font-semibold text-codialis-mint-clair hover:underline"
                        >
                          Voir le questionnaire
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}

              {onglet === 'notes' && (
                <div role="tabpanel" id="panneau-notes" aria-labelledby="onglet-notes">
                  <h2 className="font-display mb-3 text-xl font-bold text-codialis-navy">
                    Mes notes personnelles
                  </h2>
                  <MesNotes />
                </div>
              )}

              {onglet === 'ressources' && (
                <div role="tabpanel" id="panneau-ressources" aria-labelledby="onglet-ressources">
                  <h2 className="font-display mb-3 text-xl font-bold text-codialis-navy">
                    Ressources du module
                  </h2>
                  <BandeauIndisponible message="Les ressources téléchargeables du module ne sont pas encore disponibles." />
                </div>
              )}

              {onglet === 'transcription' && (
                <div
                  role="tabpanel"
                  id="panneau-transcription"
                  aria-labelledby="onglet-transcription"
                >
                  <h2 className="font-display mb-3 text-xl font-bold text-codialis-navy">
                    Transcription automatique
                  </h2>
                  <BandeauIndisponible message="La transcription automatique de la séquence n’est pas encore disponible." />
                </div>
              )}

              {onglet === 'discussion' && (
                <div role="tabpanel" id="panneau-discussion" aria-labelledby="onglet-discussion">
                  <h2 className="font-display mb-3 text-xl font-bold text-codialis-navy">
                    Discussion du module
                  </h2>
                  <BandeauIndisponible message="La discussion du module n’est pas encore disponible." />
                </div>
              )}
            </div>
          </div>
        </div>

        <aside className="scrollbar-thin overflow-y-auto border-gray-200 bg-white lg:border-l" aria-label="Plan de formation">
          <div className="border-b border-gray-100 p-4 sm:p-5">
            <div className="mb-2 flex items-center justify-between">
              <h3 className="font-display font-bold text-codialis-navy">Plan de formation</h3>
              {formation && (
                <span className="text-xs text-codialis-gray">
                  {formation.modules.length} module{formation.modules.length > 1 ? 's' : ''}
                </span>
              )}
            </div>
            {avancement !== null ? (
              <>
                <div className="progress-track mt-2 h-1.5 rounded-full">
                  <div
                    className="progress-bar h-full rounded-full"
                    style={{ width: `${avancement}%` }}
                  />
                </div>
                <div className="mt-1.5 flex justify-between text-xs text-codialis-gray">
                  <span>{avancement} % des heures du programme</span>
                  <span>{heures(emargement.heuresSuivies)} suivies</span>
                </div>
              </>
            ) : (
              <p className="mt-2 text-xs text-codialis-gray">Progression indisponible.</p>
            )}
          </div>

          {!formation ? (
            <p className="p-4 text-sm text-codialis-gray sm:p-5">
              Aucune formation n’est encore rattachée à votre dossier.
            </p>
          ) : elearning ? (
            <PlanFormation
              plan={elearning.plan}
              detailLigne={(ligne) =>
                ligne.listeReplays.length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {ligne.listeReplays.map((choisi) => (
                      <li key={choisi.id}>
                        <button
                          type="button"
                          onClick={() => lire(choisi)}
                          aria-current={replay?.id === choisi.id ? 'true' : undefined}
                          className={`flex min-h-[40px] w-full items-center gap-2 rounded-lg px-2 text-left text-xs transition ${
                            replay?.id === choisi.id ? 'bg-amber-50 font-semibold text-codialis-navy' : 'text-codialis-navy hover:bg-white'
                          }`}
                        >
                          <CirclePlay className="h-4 w-4 shrink-0 text-codialis-sky" aria-hidden />
                          <span className="min-w-0 flex-1 truncate">{choisi.titre}</span>
                          <span className="shrink-0 text-[10px] text-codialis-gray">{dateCourte(choisi.date)}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )
              }
            />
          ) : (
            <Chargement libelle="Chargement du plan de formation…" />
          )}
          <p className="flex items-start gap-1.5 px-4 pb-5 text-[11px] text-codialis-gray sm:px-5">
            <Icone nom="ampoule" className="mt-0.5 h-3 w-3 shrink-0" />
            L’avancement global est suivi en heures émargées. Le plan ne range que les classes à
            distance : les séances en présentiel n’y sont pas rattachées à un bloc.
          </p>
        </aside>
      </div>

      {enSalle && (
        <Suspense fallback={<div className="fixed inset-0 z-[60] bg-codialis-ink" />}>
          <SalleDirect
            titre={classeEnSalle?.libelle ?? 'Classe à distance'}
            sousTitre={
              classeEnSalle
                ? `${classeEnSalle.heureDebut}–${classeEnSalle.heureFin}${classeEnSalle.formateur ? ` · ${classeEnSalle.formateur}` : ''}`
                : ''
            }
            role="APPRENANT"
            obtenirAcces={() => jetonApprenant(enSalle)}
            emargement={{
              // La réponse du geste vaut avant que la liste des classes soit relue.
              emargeLe:
                classeEnSalle?.emargement.emargeLe ??
                (emarger.variables === enSalle ? (emarger.data?.emargeLe ?? null) : null),
              occupe: emarger.isPending,
              onEmarger: async () => {
                await emarger.mutateAsync(enSalle);
              },
            }}
            onQuitter={() => {
              setEnSalle(null);
              void distance.refetch();
            }}
          />
        </Suspense>
      )}
    </div>
  );
}
